import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';

import ChatChannel, { CHANNEL_LIST_PAGE_SIZE } from '../../Model/ChatChannel';
import ChannelUnreadCount from '../../Model/ChannelUnreadCount';
import useComponentMounted from '../../hooks/useComponentMounted';
import { Event } from '../../utils/events';
import ChatContext from '../ChatContext';
import ChatChannelListContext, {
  chatChannelListContextInitialState as initialState,
} from './ChatChannelListContext';

const ChatChannelListContextProvider = ({
  children,
}) => {
  const [isReady, setIsReady] = useState(initialState.isReady);
  const [channelCollectionList, setChannelCollectionList] = useState([]);
  const [unreadCountsCollection, setUnreadCountsCollection] = useState({ docs: [] });
  const [pendingChannelsCol, setPendingChannelsCol] = useState({ docs: [] });
  const [pendingChannelCount, setPendingChannelCount] = useState(initialState.pendingChannelCount);
  const [channelList, setChannelList] = useState([]);
  const [channelUnreadCounts, setChannelUnreadCounts] = useState({});
  const [isLoadingChannels, setIsLoadingChannels] = useState(initialState.isLoadingChannels);
  const [hasMoreChannels, setHasMoreChannels] = useState(initialState.hasMoreChannels);
  const [selectedBucket, setSelectedBucket] = useState(initialState.selectedBucket);
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  const [isRecentFirst, setIsRecentFirst] = useState(initialState.isRecentFirst);
  // null until the user ticks or unticks "Custom sorting"; held here so the choice outlives the Chat page.
  const [customSortChoice, setIsCustomSortEnabled] = useState(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(initialState.totalUnreadCount);
  const [claimedChannelCollection, setClaimedChannelCollection] = useState({ docs: [] });
  const {
    chatUser,
    activeChannelId,
    setActiveChannelId,
    setActiveUserId,
    logEvent,
    authUser,
    selectedPodId,
    sessionStore,
  } = useContext(ChatContext);
  const isComponentMountedRef = useComponentMounted();

  // Assistants get custom sorting until they choose otherwise. Read at render, not as the state's initial value,
  // so the default follows the session's role even if it arrives after this provider mounts.
  const isCustomSortEnabled = customSortChoice ?? !!sessionStore?.isCoachAssistant;

  /*
    This useEffect will fetch the initial channel list, pending channel list & claimed channel list
  */
  useEffect(() => {
    const init = async () => {
      const [
        initialChannelCollection,
        pendingChannels,
        claimedChannelCol,
      ] = await Promise.all([
        ChatChannel.getChannelList(chatUser.id, {
          bucket: selectedBucket,
          searchTerm,
          isRecentFirst,
          selectedPodId,
        }),
        ChatChannel.getPendingChannelList(chatUser.id, selectedPodId),
        ChatChannel.getClaimedChannelList(chatUser.id),
      ]);

      if (isComponentMountedRef.current) {
        setChannelCollectionList([initialChannelCollection]);
        setHasMoreChannels(initialChannelCollection.docs.length === CHANNEL_LIST_PAGE_SIZE);
        setPendingChannelsCol(pendingChannels);
        setClaimedChannelCollection(claimedChannelCol);
        setIsReady(true);
      }
    };

    if (!isReady && chatUser.id) {
      init();
    }
  }, [
    chatUser.id,
    isReady,
    isComponentMountedRef,
    selectedBucket,
    searchTerm,
    isRecentFirst,
    selectedPodId,
  ]);

  /*
    This useEffect will fetch the unread counts for the channels
  */
  useEffect(() => {
    const initUnreadCounts = async () => {
      const unreadCounts = await ChannelUnreadCount.getUnreadCounts(chatUser.id);

      if (isComponentMountedRef.current) {
        setUnreadCountsCollection(unreadCounts);
      }
    };

    if (chatUser.id) {
      initUnreadCounts();
    }
  }, [
    chatUser.id,
    isComponentMountedRef,
  ]);

  /*
    This useEffect will reset the isReady state every time the selectedBucket,
    searchTerm, isRecentFirst, or selectedPodId changes to refetch the channel list based
    on the new parameters
  */
  useEffect(() => {
    setIsReady(false);
  }, [
    selectedBucket,
    searchTerm,
    isRecentFirst,
    selectedPodId,
  ]);

  /*
    This useEffect will create a new list containing all channels every time the
    channelCollectionList changes
  */
  useEffect(() => {
    const disposer = autorun(() => {
      const channelDocMap = new Map();
      /*
        We need to create a map to avoid duplicate channels
        In case of duplicate channels, we log an event to track it
      */
      channelCollectionList.forEach((channelCollection) => {
        channelCollection.docs.forEach((doc) => {
          if (!channelDocMap.has(doc.id)) {
            channelDocMap.set(doc.id, doc);
          } else {
            logEvent(Event.DUPLICATE_CHANNEL, {
              channelId: doc.id,
              userId: authUser.id,
              coachId: chatUser.id,
            });
          }
        });
      });

      const channelDocList = Array.from(channelDocMap.values());
      channelDocList.sort((a, b) => Number(b.isPinned) - Number(a.isPinned));
      setPendingChannelCount(pendingChannelsCol.docs.length);
      setChannelList(channelDocList);
    });

    return () => disposer();
  }, [
    channelCollectionList,
    pendingChannelsCol,
    authUser.id,
    chatUser.id,
    logEvent,
  ]);

  /*
    This useEffect will create a new object every time the unreadCountsCollection changes
    with channel id as key and unread count as value.
    It also filters the unread counts based on the currently displayed channels (pod-filtered).
  */
  useEffect(() => {
    const disposer = autorun(() => {
      const unreadCounts = {};
      let totalUnread = 0;

      // Create a set of channel IDs from the current channel list (already pod-filtered)
      const visibleChannelIds = new Set(channelList.map((channel) => channel.id));

      unreadCountsCollection.docs.forEach((unreadCountDoc) => {
        const { channelId, unreadCount } = unreadCountDoc;

        // Only include unread counts for channels that are visible in the current pod
        if (visibleChannelIds.has(channelId)) {
          unreadCounts[channelId] = unreadCount;
          totalUnread += unreadCount || 0;
        }
      });

      setChannelUnreadCounts({ ...unreadCounts });
      setTotalUnreadCount(totalUnread);
    });

    return () => disposer();
  }, [
    unreadCountsCollection,
    channelList,
  ]);

  /*
    This useMemo builds a mapping from userId (client) to their unread message count.
    It allows any component to look up a client's unread count in O(1) without
    needing to know the channelId.
  */
  const userUnreadCounts = useMemo(() => {
    const counts = {};
    channelList.forEach((channel) => {
      const count = channelUnreadCounts[channel.id];
      if (channel.userId && count > 0) {
        counts[channel.userId] = count;
      }
    });
    return counts;
  }, [channelList, channelUnreadCounts]);

  const loadMoreChannels = useCallback(async () => {
    setIsLoadingChannels(true);
    const lastChannelCollection = channelCollectionList[channelCollectionList.length - 1];
    const nextChannelList = await ChatChannel.getChannelList(chatUser.id, {
      startAfterDoc: lastChannelCollection.docs[lastChannelCollection.docs.length - 1],
      searchTerm,
      bucket: selectedBucket,
      isRecentFirst,
      selectedPodId,
    });

    if (isComponentMountedRef.current) {
      if (nextChannelList.docs.length < CHANNEL_LIST_PAGE_SIZE) {
        setHasMoreChannels(false);
      }
      setIsLoadingChannels(false);
      setChannelCollectionList((prevChannelList) => ([
        ...prevChannelList,
        nextChannelList,
      ]));
    }
  }, [
    channelCollectionList,
    chatUser.id,
    isComponentMountedRef,
    searchTerm,
    selectedBucket,
    isRecentFirst,
    selectedPodId,
  ]);

  const archiveChats = useCallback(
    () => ChatChannel.archiveAllChats(chatUser.id, selectedPodId),
    [chatUser.id, selectedPodId],
  );

  const context = useMemo(() => ({
    isReady,
    setIsReady,
    channelList,
    channelUnreadCounts,
    loadMoreChannels,
    activeChannelId,
    setActiveChannelId,
    isLoadingChannels,
    hasMoreChannels,
    selectedBucket,
    setSelectedBucket,
    searchTerm,
    setSearchTerm,
    isRecentFirst,
    setIsRecentFirst,
    isCustomSortEnabled,
    setIsCustomSortEnabled,
    setActiveUserId,
    totalUnreadCount,
    unreadCountsCollection,
    archiveChats,
    claimedChannelCollection,
    pendingChannelCount,
    userUnreadCounts,
  }), [
    isReady,
    setIsReady,
    channelList,
    channelUnreadCounts,
    loadMoreChannels,
    activeChannelId,
    setActiveChannelId,
    isLoadingChannels,
    hasMoreChannels,
    selectedBucket,
    setSelectedBucket,
    searchTerm,
    setSearchTerm,
    isRecentFirst,
    setIsRecentFirst,
    isCustomSortEnabled,
    setIsCustomSortEnabled,
    setActiveUserId,
    totalUnreadCount,
    unreadCountsCollection,
    archiveChats,
    claimedChannelCollection,
    pendingChannelCount,
    userUnreadCounts,
  ]);

  return (
    <ChatChannelListContext.Provider value={context}>
      {children}
    </ChatChannelListContext.Provider>
  );
};

ChatChannelListContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(ChatChannelListContextProvider);
